import { useAuth } from '../../contexts/AuthContext';
import { PresaleCodes, usePresaleCodes, usePresaleModerator } from './PresaleCodes';
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { WaitlistClaimCode } from '../../components/WaitlistClaimCode';
import { accountIdentity } from '../../components/accountIdentity';
import { v2Path } from '../nav';
import { useAccounts, useUpcomingSales, useVenues } from '../../hooks/useApi';
import { UpcomingSaleEvent } from '../../types';
import {
  Badge,
  EmptyState,
  PageHeader,
  PageShell,
  SegmentedControl,
  SkeletonCard,
  cx,
} from '../components';
import { CalendarIcon, SearchIcon } from '../components/icons';
import { FOCUS_RING } from '../components/styles';

const SELECT_CLASS = cx(
  'rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-sm text-white',
  'focus:border-[#3b82f6]/50 focus:outline-none',
  FOCUS_RING,
);

/**
 * Minor units, always — `min_price: 2833` is $28.33 and the API never converts.
 * Currency is nullable, so it falls back rather than rendering "null".
 */
const formatPrice = (minorUnits: number, currency: string | null) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'USD',
  }).format(minorUnits / 100);

const priceLabel = (event: UpcomingSaleEvent): string | null => {
  const { min_price: min, max_price: max, currency } = event;
  if (min == null && max == null) return null;
  if (min != null && max != null && min !== max) {
    return `${formatPrice(min, currency)} – ${formatPrice(max, currency)}`;
  }
  const one = min ?? max;
  return one == null ? null : formatPrice(one, currency);
};

/**
 * These timestamps are true UTC instants ending in Z, unlike the sold-out
 * endpoint's venue-local strings with an embedded offset. A sale opening is a
 * single moment worldwide, so rendering it in the VIEWER's zone is correct here
 * — that is the clock the seller will be watching.
 */
const formatMoment = (iso: string | null): string | null => {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });
};

const formatShowDate = (iso: string): string => formatMoment(iso) ?? iso;

/** Whole days and hours until `iso`, or null once it is in the past. */
const countdown = (iso: string | null): string | null => {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (Number.isNaN(ms) || ms <= 0) return null;
  const hours = Math.floor(ms / 3_600_000);
  if (hours < 1) return `in ${Math.max(1, Math.round(ms / 60_000))} min`;
  if (hours < 48) return `in ${hours}h`;
  return `in ${Math.floor(hours / 24)} days`;
};

/**
 * The sale the list is ordered by: the presale when there is one, the general
 * onsale otherwise. Mirrors the server's sort so the label always names the
 * moment that put the row where it is.
 */
const nextSale = (event: UpcomingSaleEvent): { label: string; at: string } | null => {
  if (event.pre_sale_start_date) return { label: 'Presale', at: event.pre_sale_start_date };
  if (event.sale_start_date) return { label: 'On sale', at: event.sale_start_date };
  return null;
};

/**
 * The three states of `is_fully_locked`, which is null for roughly two rows in
 * three. Null must never read as "no code needed" — that would tell a seller a
 * gated presale is open to them.
 */
const lockBadge = (locked: boolean | null) => {
  if (locked === true) return <Badge tone="amber" size="sm">Access code required</Badge>;
  if (locked === false) return <Badge tone="green" size="sm">No code needed</Badge>;
  return (
    <Badge tone="neutral" size="sm" title="This event has not been checked for access codes">
      Code status unknown
    </Badge>
  );
};

type LockFilter = 'all' | 'open' | 'locked';

export const UpcomingSalesScreen: React.FC = () => {
  const { user, isAdminSession } = useAuth();
  const identity = isAdminSession ? `admin:${localStorage.getItem('flashtools_admin_email') || 'session'}` : user?.uid || '';
  return <UpcomingSalesContent key={identity} identity={identity} />;
};

const UpcomingSalesContent: React.FC<{ identity: string }> = ({ identity }) => {
  const codes = usePresaleCodes(identity);
  const moderator = usePresaleModerator(identity);
  const accountQuery = useAccounts();
  const venuesQuery = useVenues();
  const claimAccounts = (accountQuery.data || [])
    .filter(account => account.platform === 'DICE' && account.is_active)
    .map(account => ({ id: account.id, label: accountIdentity(account) }));
  const refreshCodes = async () => {
    const result = await codes.refetch();
    if (result.error) throw result.error;
  };
  const [lockFilter, setLockFilter] = useState<LockFilter>('all');
  const [subscribedOnly, setSubscribedOnly] = useState(false);
  const [city, setCity] = useState('');

  // Server-side access/subscription filtering: `locked=false` is implemented by Django
  // (the upstream Go service only understands `true`), and it applies `limit`
  // AFTER filtering — so asking for it beats filtering what arrives.
  const { data, isLoading, error, refetch, isFetching } = useUpcomingSales({
    locked: lockFilter === 'all' ? undefined : lockFilter === 'locked',
    subscribed: subscribedOnly || undefined,
  });

  const cityByVenue = useMemo(() => new Map(
    (venuesQuery.data || []).map(venue => [String(venue.venue_id), venue.city?.trim() || '']),
  ), [venuesQuery.data]);
  const cities = useMemo(() => {
    const names = new Map<string, string>();
    for (const event of data || []) {
      const name = cityByVenue.get(String(event.venue_id));
      if (name && !names.has(name.toLowerCase())) names.set(name.toLowerCase(), name);
    }
    return Array.from(names.values()).sort((a, b) => a.localeCompare(b));
  }, [data, cityByVenue]);
  const selectedCity = cities.includes(city) ? city : '';
  // Filter locally so picking a city does not remove the other dropdown options.
  const events = useMemo(() => (data || []).filter(event => !selectedCity ||
    cityByVenue.get(String(event.venue_id))?.toLowerCase() === selectedCity.toLowerCase()),
  [data, selectedCity, cityByVenue]);

  const status = (error as { status?: number } | null)?.status;
  // 503/504 are the upstream Go service, not this user and not a bad request.
  const upstreamDown = status === 503 || status === 504;

  return (
    <PageShell>
      <PageHeader
        title="Upcoming Sales"
        subtitle="Presales and onsales that haven't opened yet, soonest first."
        meta={
          events.length > 0
            ? `${events.length} event${events.length === 1 ? '' : 's'} with a sale still to come`
            : undefined
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <SegmentedControl
          label="Access code"
          size="sm"
          value={lockFilter}
          onChange={(v) => setLockFilter(v as LockFilter)}
          options={[
            { value: 'all', label: 'All' },
            { value: 'locked', label: 'Code required' },
            { value: 'open', label: 'No code required' },
          ]}
        />

        <button
          type="button"
          aria-pressed={subscribedOnly}
          onClick={() => setSubscribedOnly((v) => !v)}
          className={cx(
            'rounded-lg border px-3 py-1.5 text-sm transition-colors',
            subscribedOnly
              ? 'border-[#3b82f6] bg-[#3b82f6]/10 text-white'
              : 'border-white/10 bg-white/5 text-white/70 hover:bg-white/10',
            FOCUS_RING,
          )}
        >
          Subscribed Event
        </button>

        <select
          value={selectedCity}
          onChange={(e) => setCity(e.target.value)}
          aria-label="City"
          className={SELECT_CLASS}
        >
          <option value="">All Cities</option>
          {cities.map(name => <option key={name} value={name}>{name}</option>)}
        </select>
        {venuesQuery.isError && <button type="button" className="text-xs text-amber-200" onClick={() => venuesQuery.refetch()}>Could not load cities — retry</button>}

      </div>

      {isLoading && (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => <SkeletonCard key={i} />)}
        </div>
      )}

      {/* The upstream being down is worth saying plainly — it is neither an
          empty result nor anything the seller can fix by changing filters. */}
      {!isLoading && error && (
        <EmptyState
          tone="error"
          icon={<CalendarIcon className="h-5 w-5" />}
          title={upstreamDown ? 'Sale data is temporarily unavailable' : 'Could not load upcoming sales'}
          body={
            upstreamDown
              ? "The service that tracks sale dates isn't responding. Nothing is wrong with your account — try again shortly."
              : (error as Error).message
          }
          action={
            <button
              type="button"
              onClick={() => refetch()}
              disabled={isFetching}
              className={cx(
                'rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-sm text-white/80',
                'transition-colors hover:bg-white/10 disabled:opacity-50',
                FOCUS_RING,
              )}
            >
              {isFetching ? 'Retrying...' : 'Try again'}
            </button>
          }
        />
      )}

      {/* Built for first, not last: most rows have no recorded sale date, so an
          empty list is the routine answer rather than a symptom. */}
      {!isLoading && !error && events.length === 0 && (
        <EmptyState
          icon={<SearchIcon className="h-5 w-5" />}
          title="Nothing on sale soon"
          body="No events match these filters. Try the other code filter or change the subscription and city filters."
        />
      )}

      {!isLoading && !error && events.length > 0 && (
        <div className="space-y-3">
          {events.map((event) => {
            const sale = nextSale(event);
            const saleAt = formatMoment(sale?.at ?? null);
            const soon = countdown(sale?.at ?? null);
            const price = priceLabel(event);
            // Only worth showing when a presale precedes it; otherwise the
            // general onsale IS the headline date above.
            const generalOnsale = event.pre_sale_start_date
              ? formatMoment(event.sale_start_date)
              : null;

            return (
              <article
                key={event.id}
                className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4 sm:p-5"
              >
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between sm:gap-5">
                  <div className="flex min-w-0 gap-3 sm:gap-4">
                    {event.image_url && (
                      <img
                        src={event.image_url}
                        alt={event.name}
                        className="h-16 w-16 flex-shrink-0 rounded-lg object-cover sm:h-20 sm:w-20"
                      />
                    )}

                    <div className="flex min-w-0 flex-1 flex-col gap-1">
                      <a
                        href={event.source_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={cx(
                          'truncate rounded font-semibold text-white transition-colors hover:text-blue-400',
                          FOCUS_RING,
                        )}
                      >
                        {event.name}
                      </a>

                      <span className="text-sm text-white/60">
                        {formatShowDate(event.event_start_date)}
                      </span>

                      <div className="mt-1 flex flex-wrap items-center gap-2">
                        {lockBadge(event.is_fully_locked)}
                        {price && (
                          <span className="text-sm text-white/60 tabular-nums">{price}</span>
                        )}
                        {event.max_tickets != null && (
                          <span className="text-xs text-white/40">
                            {event.max_tickets} per order
                          </span>
                        )}
                        <span className="text-xs uppercase tracking-wide text-white/30">
                          {event.source}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* The sale moment, which is also what put this row in this
                      position — the list is ordered by it, not by show date. */}
                  <div className="flex flex-shrink-0 flex-col gap-1 sm:items-end">
                    {sale && saleAt ? (
                      <>
                        <span className="flex items-center gap-2">
                          <Badge tone={sale.label === 'Presale' ? 'blue' : 'neutral'} size="sm">
                            {sale.label}
                          </Badge>
                          {soon && (
                            <span className="text-xs text-white/50 tabular-nums">{soon}</span>
                          )}
                        </span>
                        <span className="text-sm text-white/80">{saleAt}</span>
                        {generalOnsale && (
                          <span className="text-xs text-white/40">
                            General onsale {generalOnsale}
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="text-sm text-white/40">Sale date not announced</span>
                    )}
                  </div>
                </div>
                {event.source.toLowerCase() === 'dice' && (
                  <section aria-label="Claim code" className="mt-4 border-t border-white/10 pt-4">
                    <h3 className="text-sm font-semibold text-white">Claim code</h3>
                    <p className="mt-1 text-xs text-white/50">Have a DICE claim code? Choose the account to apply it to.</p>
                    {accountQuery.isLoading ? (
                      <p className="mt-2 text-sm text-white/60">Loading DICE accounts…</p>
                    ) : accountQuery.error ? (
                      <div role="alert" className="mt-2 text-sm text-amber-200">
                        Could not load your accounts.{' '}
                        <button type="button" className={cx('rounded underline', FOCUS_RING)} disabled={accountQuery.isFetching} onClick={() => accountQuery.refetch()}>Retry</button>
                      </div>
                    ) : claimAccounts.length === 0 ? (
                      <Link to={v2Path('/accounts')} className={cx('mt-2 inline-block rounded text-sm text-blue-400 hover:underline', FOCUS_RING)}>Connect a DICE account to claim a code</Link>
                    ) : (
                      <WaitlistClaimCode eventId={event.event_id} accounts={claimAccounts}
                        accountsPath={v2Path('/accounts')} billingPath={v2Path('/billing')} />
                    )}
                  </section>
                )}
                <PresaleCodes canDelete={!moderator.isError && moderator.data?.is_superuser === true} eventId={event.id} sourceUrl={event.source_url}
                  presales={codes.data || []}
                  loading={codes.isLoading} error={codes.error} refresh={refreshCodes} />
              </article>
            );
          })}
        </div>
      )}
    </PageShell>
  );
};
